import {assertUnreachable} from '@wandb/weave/common/util/types';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef} from 'react';
import {Input} from 'semantic-ui-react';

import * as Filters from '../../../util/filters';
import type * as FilterTypes from '../../../util/filterTypes';
import type * as RunTypes from '../../../util/runTypes';
import {useDebouncedValue} from '../../../util/useDebouncedValue';
import {
  type FilterValueSelectorCreatorProps,
  FilterValueSelectorDate,
  FilterValueSelectorTime,
} from '../../Filters/FilterValueSelector';
import {FilterSimpleValueInfiniteDropdown} from '../../InfiniteScrollDropdown/FilterSimpleValueComponents';
import {useRunFilterValueDropdown} from '../hooks/useRunFilterValueDropdown';
import {
  isSweepFilterKey,
  SweepValueInfiniteDropdown,
} from '../SweepValueInfiniteDropdown';
import {RunsFilterTagNameSelector} from './RunsFilterTagNameSelector';

export type RunFilterValueSelectorCreatorProps =
  FilterValueSelectorCreatorProps<RunTypes.Key>;

type RunFilterValueSelectorProps = RunFilterValueSelectorCreatorProps & {
  entityName: string;
  projectName: string;
};

export function RunsFilterContainsInput({
  filter,
  setFilter,
}: {
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter: (f: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
}) {
  const initialValue = String(filter.value ?? '');
  const filterRef = useRef(filter);
  const setFilterRef = useRef(setFilter);
  // oxlint-disable-next-line react/refs
  filterRef.current = filter;
  // oxlint-disable-next-line react/refs
  setFilterRef.current = setFilter;
  const onDebouncedChange = useCallback((value: string) => {
    setFilterRef.current({
      ...filterRef.current,
      value,
      disabled: value === '',
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  }, []);
  const {value, debouncedSetValue, instantSetValue} = useDebouncedValue(
    initialValue,
    300,
    onDebouncedChange
  );

  useEffect(() => {
    instantSetValue(initialValue);
  }, [initialValue, instantSetValue]);

  return (
    <Input
      className="filter-list__value"
      data-test="filter-value"
      placeholder="Substring to match"
      value={value}
      onChange={(_, {value: v}) => debouncedSetValue(v ?? '')}
      style={{flexGrow: 1, maxWidth: 400}}
    />
  );
}

export const RunsFilterValueSelector = ({
  entityName,
  projectName,
  filter,
  setFilter,
  isValid = true,
}: RunFilterValueSelectorProps) => {
  const setPartial = (
    partialFilter: Partial<FilterTypes.IndividualFilter<RunTypes.Key>>
  ) => {
    setFilter({
      ...filter,
      ...partialFilter,
    } as FilterTypes.IndividualFilter<RunTypes.Key>);
  };

  const filterValueType = Filters.getFilterValueType(filter);
  switch (filterValueType) {
    case Filters.ValueType.Duration:
      return (
        <FilterValueSelectorTime
          meta={Filters.assertIsValueFilter(filter).meta}
          value={filter.value as string}
          setFilter={setPartial}
          minimumUnit={
            filter.op === 'WITHINSECONDS' ? 'minutes' : null
            /* the withinseconds operation isn't guaranteed to be accurate
                 on the scale of seconds and there isn't a known use case. */
          }
        />
      );
    case Filters.ValueType.Date:
      return (
        <FilterValueSelectorDate
          value={filter.value as string}
          setFilter={setPartial}
        />
      );
    case Filters.ValueType.Tags: {
      return (
        <RunsFilterTagNameSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter as FilterTypes.ValueFilter<RunTypes.Key>}
          setFilter={setPartial}
        />
      );
    }
    case Filters.ValueType.Default:
      if (filter.op === 'CONTAINS') {
        return (
          <RunsFilterContainsInput filter={filter} setFilter={setFilter} />
        );
      }
      return (
        <RunsFilterSimpleValueSelector
          entityName={entityName}
          projectName={projectName}
          filter={filter}
          setFilter={setFilter}
          isValid={isValid}
        />
      );
    default:
      assertUnreachable(filterValueType);
  }
};

type RunsFilterSimpleValueSelectorProps = {
  entityName: string;
  projectName: string;
  filter: FilterTypes.IndividualFilter<RunTypes.Key>;
  setFilter(filter: FilterTypes.IndividualFilter<RunTypes.Key>): void;
  isValid?: boolean;
};
const RunsFilterSimpleValueSelector = (
  props: RunsFilterSimpleValueSelectorProps
) => {
  const {filter, setFilter, isValid = true, entityName, projectName} = props;
  const dropdown = useRunFilterValueDropdown({filter, setFilter});

  if (dropdown.shouldRenderNull) {
    return <TailwindContents />;
  }

  if (isSweepFilterKey(filter)) {
    return (
      <TailwindContents>
        <SweepValueInfiniteDropdown
          entityName={entityName}
          projectName={projectName}
          forceExactMatch={['=', '!='].includes(filter.op)}
          skip={dropdown.keyPath === ''}
          isMultiSelect={dropdown.isMultiSelect}
          selections={dropdown.selections}
          toggleSelection={dropdown.toggleSelection}
          isValid={isValid}
        />
      </TailwindContents>
    );
  }

  return (
    <TailwindContents>
      <FilterSimpleValueInfiniteDropdown
        entityName={entityName}
        projectName={projectName}
        forceExactMatch={['=', '!='].includes(filter.op)}
        extraQueryVariables={dropdown.extraQueryVariables}
        skip={dropdown.keyPath === ''}
        isMultiSelect={dropdown.isMultiSelect}
        selections={dropdown.selections}
        toggleSelection={dropdown.toggleSelection}
        isValid={isValid}
      />
    </TailwindContents>
  );
};
